import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Category = () => {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Simulate API call to fetch category details
    const fetchCategoryData = async () => {
      try {
        setLoading(true);
        
        // Mock data - in a real app, you would fetch from your API
        setTimeout(() => {
          // Simulate a category
          const mockCategory = {
            id: id,
            name: `Game Category ${id}`,
            description: 'This is a game category for exciting competitive gameplay.',
            imageUrl: `https://picsum.photos/seed/${id}/800/400`,
            followers: 12453,
            streamCount: 42,
          };
          
          // Simulate streams in this category
          const mockStreams = Array.from({ length: 8 }, (_, i) => ({
            id: `stream-${i}`,
            title: `Awesome Stream ${i + 1} in ${mockCategory.name}`,
            streamer: {
              id: `user-${i}`,
              username: `streamer${i}`,
              displayName: `Streamer ${i}`,
              profileImage: `https://i.pravatar.cc/150?u=streamer${i}`,
            },
            thumbnail: `https://picsum.photos/seed/stream${i}/400/225`,
            viewers: Math.floor(Math.random() * 10000),
            tags: ['competitive', 'esports', i % 2 === 0 ? 'tournament' : 'casual'],
            startedAt: new Date(Date.now() - Math.floor(Math.random() * 10000000)).toISOString(),
          }));
          
          setCategory(mockCategory);
          setStreams(mockStreams);
          setLoading(false);
        }, 1000);
      } catch (err) {
        console.error('Error fetching category data:', err);
        setError('Failed to load category data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchCategoryData();
  }, [id]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <Link
            to="/categories"
            className="inline-block py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Go Back to Categories
          </Link>
        </div>
      </div>
    );
  }
  
  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Category Not Found</h2>
          <p className="text-gray-700 mb-6">The category you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/categories"
            className="inline-block py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Browse All Categories
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pt-16 pb-12">
      {/* Category header */}
      <div 
        className="relative h-64 sm:h-80 bg-cover bg-center mb-8"
        style={{ backgroundImage: `url(${category.imageUrl})` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 flex flex-col justify-end pb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{category.name}</h1>
          <p className="text-lg text-white/80 max-w-3xl">{category.description}</p>
          <div className="flex mt-4 space-x-4">
            <div className="text-white">
              <span className="font-bold">{category.followers.toLocaleString()}</span> followers
            </div>
            <div className="text-white">
              <span className="font-bold">{category.streamCount}</span> live streams
            </div>
          </div>
        </div>
      </div>
      
      {/* Live streams */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Live Streams</h2>
          <div className="flex space-x-2">
            <select className="py-1 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
              <option>Recommended</option>
              <option>Most Viewers</option>
              <option>Recently Started</option>
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {streams.map((stream) => (
            <motion.div 
              key={stream.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow overflow-hidden"
            >
              <Link to={`/stream/${stream.id}`} className="block relative">
                <img 
                  src={stream.thumbnail} 
                  alt={stream.title} 
                  className="w-full aspect-video object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded">
                  LIVE
                </div>
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                  {stream.viewers.toLocaleString()} viewers
                </div>
              </Link>
              <div className="p-4">
                <div className="flex items-start space-x-3">
                  <img 
                    src={stream.streamer.profileImage} 
                    alt={stream.streamer.displayName} 
                    className="w-10 h-10 rounded-full flex-shrink-0"
                  />
                  <div>
                    <h3 className="font-medium text-gray-900 line-clamp-1" title={stream.title}>
                      {stream.title}
                    </h3>
                    <Link to={`/user/${stream.streamer.username}`} className="text-sm text-gray-600 hover:text-blue-600">
                      {stream.streamer.displayName}
                    </Link>
                    <div className="flex flex-wrap mt-2 gap-1">
                      {stream.tags.map((tag) => (
                        <span key={tag} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Category; 